import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatCard from './components/StatCard';
import UserManagementTable from './components/UserManagementTable';
import EventManagementPanel from './components/EventManagementPanel';
import RevenueChart from './components/RevenueChart';
import ContentModerationPanel from './components/ContentModerationPanel';

import DetailedEventManagement from './components/DetailedEventManagement';
import StoreManager from '../../components/admin/StoreManager';
import SpotManagement from './SpotManagement';
import BlogManagement from './components/BlogManagement';
import ThemeLogoManagement from './components/ThemeLogoManagement';
import EmailTemplateManager from './components/EmailTemplateManager';

import { adminService } from '../../services/adminService';
import { marketplaceService } from '../../services/marketplaceService';
import ModerationTab from './components/ModerationTab';
import { supabase } from '../../lib/supabase';
import Community from '../community';
import UserProfileModal from '../community/components/UserProfileModal';
import MasterInvoiceCenter from '../master-invoice-center';
import InvoiceAuditDashboard from '../invoice-audit-dashboard';
import EventInvoiceComplianceDashboard from '../event-invoice-compliance';
import CouponManagement from './components/CouponManagement';
import MysoftSyncLogs from './components/MysoftSyncLogs';

const navCategories = [
  {
    id: 'dashboard-group',
    label: 'Dashboard',
    emoji: '📊',
    items: [
      { id: 'overview', label: 'Genel Bakış', icon: 'LayoutDashboard' },
    ],
  },
  {
    id: 'operation-group',
    label: 'Operasyon',
    emoji: '🏄‍♂️',
    items: [
      { id: 'event-management', label: 'Etkinlik Yönetimi', icon: 'CalendarCog' },
      { id: 'store-management', label: 'Mağaza Yönetimi', icon: 'Store' },
      { id: 'spot-management', label: 'Spot Yönetimi', icon: 'MapPin' },
      { id: 'coupon-management', label: 'Kupon Yönetimi', icon: 'Tag' },
    ],
  },
  {
    id: 'finance-group',
    label: 'Finans',
    emoji: '💰',
    items: [
      { id: 'master-invoice-center', label: 'Fatura Merkezi', icon: 'LayoutList' },
      { id: 'invoice-audit', label: 'Mağaza Faturaları', icon: 'Receipt' },
      { id: 'invoice-compliance', label: 'Etkinlik Faturaları', icon: 'ClipboardList' },
      { id: 'mysoft-logs', label: 'Mysoft Sync Logları', icon: 'RefreshCw' },
    ],
  },
  {
    id: 'community-group',
    label: 'Topluluk & İçerik',
    emoji: '👥',
    items: [
      { id: 'users', label: 'Kullanıcılar', icon: 'Users' },
      { id: 'community', label: 'Topluluk', icon: 'Globe' },
      { id: 'blog', label: 'Blog', icon: 'FileText' },
      { id: 'review-moderation', label: 'Yorum Moderasyonu', icon: 'MessageSquare' },
      { id: 'moderation', label: 'Moderasyon', icon: 'Shield' },
    ],
  },
  {
    id: 'system-group',
    label: 'Sistem',
    emoji: '⚙️',
    items: [
      { id: 'theme-logo', label: 'Tema & Logo', icon: 'Palette' },
      { id: 'email-templates', label: 'E-posta Şablonları', icon: 'Mail' },
    ],
  },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeEvents: 0,
    pendingApprovals: 0,
    totalRevenue: 0,
    storeRevenue: 0,
    eventRevenue: 0
  });
  const [users, setUsers] = useState([]);
  const [events, setEvents] = useState([]);
  const [revenueData, setRevenueData] = useState([]);
  const [moderationItems, setModerationItems] = useState({ events: [], marketplace: [] });
  const [error, setError] = useState('');
  const [recentActivities, setRecentActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [selectedCommunityUserId, setSelectedCommunityUserId] = useState(null);

  // Historical filter state
  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(now?.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now?.getMonth());
  const [isCurrentMonth, setIsCurrentMonth] = useState(true);
  const [allTimeRevenue, setAllTimeRevenue] = useState({ allTimeTotal: 0, allTimeStoreRevenue: 0, allTimeEventRevenue: 0 });
  const [loadingStats, setLoadingStats] = useState(false);

  // Monthly Revenue Detail Modal state
  const [showRevenueDetailModal, setShowRevenueDetailModal] = useState(false);
  const [revenueTransactions, setRevenueTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);

  useEffect(() => {
    const userRole = localStorage.getItem('userRole');
    if (userRole !== 'admin') {
      navigate('/event-discovery');
      return;
    }
    loadDashboardData();
  }, [navigate]);

  const loadDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');
      const [statsData, revenueChartData, allTimeData] = await Promise.all([
        adminService?.getDashboardStats(),
        adminService?.getRevenueChartData(),
        adminService?.getAllTimeRevenue()
      ]);
      setStats(statsData);
      setRevenueData(revenueChartData);
      setAllTimeRevenue(allTimeData);
      await loadRecentActivities();
    } catch (err) {
      console.error('Dashboard load error:', err);
      setError(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRecentActivities = async () => {
    try {
      setLoadingActivities(true);
      const { data: newUsers, error: usersError } = await supabase?.from('user_profiles')?.select('id, full_name, email, created_at')?.order('created_at', { ascending: false })?.limit(5);
      if (usersError) throw usersError;
      const { data: newEvents, error: eventsError } = await supabase?.from('events')?.select('id, title, created_at')?.order('created_at', { ascending: false })?.limit(5);
      if (eventsError) throw eventsError;
      const userActivities = (newUsers || [])?.map(user => ({
        type: 'user',
        icon: 'UserPlus',
        text: `Yeni Kullanıcı: ${user?.full_name || user?.email || 'İsimsiz'}`,
        time: formatRelativeTime(user?.created_at),
        color: 'text-success',
        createdAt: new Date(user?.created_at)
      }));
      const eventActivities = (newEvents || [])?.map(event => ({
        type: 'event',
        icon: 'Calendar',
        text: `Yeni Etkinlik: ${event?.title}`,
        time: formatRelativeTime(event?.created_at),
        color: 'text-primary',
        createdAt: new Date(event?.created_at)
      }));
      const combined = [...userActivities, ...eventActivities]?.sort((a, b) => b?.createdAt - a?.createdAt)?.slice(0, 5);
      setRecentActivities(combined);
    } catch (err) {
      console.error('Error loading recent activities:', err);
    } finally {
      setLoadingActivities(false);
    }
  };

  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return 'Bilinmiyor';
    const now = new Date();
    const past = new Date(timestamp);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Az önce';
    if (diffMins < 60) return `${diffMins} dakika önce`;
    if (diffHours < 24) return `${diffHours} saat önce`;
    if (diffDays < 7) return `${diffDays} gün önce`;
    return past?.toLocaleDateString('tr-TR');
  };

  const handleDownloadUserCSV = async () => {
    try {
      const { data: users, error } = await supabase?.from('user_profiles')?.select('first_name, last_name, email, phone, location, experience_level, has_equipment, created_at')?.order('created_at', { ascending: false });
      if (error) throw error;
      if (!users || users?.length === 0) { alert('İndirilecek kullanıcı bulunamadı'); return; }
      const headers = ['Ad', 'Soyad', 'E-posta', 'Telefon', 'Konum', 'Deneyim', 'Ekipman', 'Kayıt Tarihi'];
      const rows = users?.map(u => [
        u?.first_name || '-', u?.last_name || '-', u?.email,
        u?.phone ? `="${u?.phone}"` : '-', u?.location || '-',
        u?.experience_level || '-', u?.has_equipment || '-',
        new Date(u.created_at)?.toLocaleDateString('tr-TR')
      ]);
      const csvString = [headers?.join(';'), ...rows?.map(row => row?.join(';'))]?.join('\n');
      const fullString = "sep=;\n" + csvString;
      const encoder = new TextEncoder();
      const csvData = encoder?.encode(fullString);
      const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
      const blob = new Blob([BOM, csvData], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link?.setAttribute('download', `uyeler_listesi_${new Date()?.toISOString()?.slice(0,10)}.csv`);
      document.body?.appendChild(link);
      link?.click();
      document.body?.removeChild(link);
      URL.revokeObjectURL(url);
      alert('Kullanıcı listesi başarıyla indirildi!');
    } catch (error) {
      console.error("Export error:", error);
      alert("Rapor indirilemedi: " + error?.message);
    }
  };

  const handleBroadcastNotification = async () => {
    const message = prompt('Tüm kullanıcılara gönderilecek duyuru mesajını girin:');
    if (!message || message?.trim() === '') return;
    try {
      const { data: allUsers, error: usersError } = await supabase?.from('user_profiles')?.select('id');
      if (usersError) throw usersError;
      if (!allUsers || allUsers?.length === 0) { alert('Bildirim gönderilecek kullanıcı bulunamadı'); return; }
      const notifications = allUsers?.map(user => ({
        user_id: user?.id,
        title: '📢 Platform Duyurusu',
        message: message?.trim(),
        type: 'info',
        action_url: '/',
        read: false
      }));
      const { error: insertError } = await supabase?.from('notifications')?.insert(notifications);
      if (insertError) throw insertError;
      alert(`Duyuru ${allUsers?.length} kullanıcıya başarıyla gönderildi!`);
    } catch (err) {
      console.error('Broadcast notification error:', err);
      alert('Duyuru gönderme hatası: ' + err?.message);
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await adminService?.getAllUsers();
      setUsers(usersData);
    } catch (err) {
      console.error('Load users error:', err);
      setError(err?.message);
    }
  };

  const loadModerationItems = async () => {
    try {
      const pendingContent = await adminService?.getPendingContent();
      setModerationItems(pendingContent);
    } catch (err) {
      console.error('Load moderation items error:', err);
      setError(err?.message);
    }
  };

  useEffect(() => {
    if (activeTab === 'users' && users?.length === 0) {
      loadUsers();
    } else if (activeTab === 'moderation' && moderationItems?.events?.length === 0 && moderationItems?.marketplace?.length === 0) {
      loadModerationItems();
    }
  }, [activeTab]);

  const handleUserAction = async (action, userId, data) => {
    try {
      switch (action) {
        case 'role':
          await adminService?.updateUserStatus(userId, { role: data?.role });
          alert('Kullanıcı rolü başarıyla güncellendi');
          break;
        case 'promote':
          const user = users?.find(u => u?.id === userId);
          const result = await adminService?.toggleProMembership(userId, user?.membershipTier);
          if (result?.success) {
            alert(result?.newTier === 'pro' ? 'Kullanıcı PRO üyeliğe yükseltildi' : 'Kullanıcı ücretsiz üyeliğe düşürüldü');
          }
          break;
        case 'ban':
          await adminService?.banUser(userId);
          alert('Kullanıcı yasaklandı');
          break;
        case 'unban':
          await adminService?.unbanUser(userId);
          alert('Kullanıcı yasağı kaldırıldı');
          break;
        case 'delete':
          await adminService?.deleteUser(userId);
          alert('Kullanıcı başarıyla silindi');
          break;
        case 'refresh':
          break;
        default:
          console.warn('Unknown action:', action);
      }
      await loadUsers();
    } catch (error) {
      console.error('User action error:', error);
      alert('İşlem başarısız: ' + error?.message);
    }
  };

  const handleModerationAction = async (action, itemId, itemType) => {
    try {
      setError('');
      if (itemType === 'marketplace') {
        if (action === 'approve') await marketplaceService?.updateListingStatus(itemId, 'active');
        else if (action === 'remove') await marketplaceService?.updateListingStatus(itemId, 'rejected');
        setModerationItems(prev => ({ ...prev, marketplace: prev?.marketplace?.filter(item => item?.id !== itemId) }));
      } else {
        await adminService?.reviewContent(itemId, itemType, action);
        setModerationItems(prev => ({ ...prev, events: prev?.events?.filter(item => item?.id !== itemId) }));
      }
    } catch (err) {
      console.error('Moderation action error:', err);
      setError(err?.message || 'İçerik onaylama işlemi başarısız oldu');
    }
  };

  const handleNavClick = (item) => {
    setActiveTab(item?.id);
    setSidebarOpen(false);
  };

  const getActiveLabel = () => {
    for (const cat of navCategories) {
      const found = cat?.items?.find(i => i?.id === activeTab);
      if (found) return found?.label;
    }
    return 'Genel Bakış';
  };

  const handleMonthFilterChange = async (year, month) => {
    const currentNow = new Date();
    const isCurrent = year === currentNow?.getFullYear() && month === currentNow?.getMonth();
    setSelectedYear(year);
    setSelectedMonth(month);
    setIsCurrentMonth(isCurrent);

    try {
      setLoadingStats(true);
      const statsData = isCurrent
        ? await adminService?.getDashboardStats()
        : await adminService?.getDashboardStatsByMonth(year, month);
      setStats(statsData);
    } catch (err) {
      console.error('Filter stats error:', err);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleOpenRevenueDetail = async () => {
    setShowRevenueDetailModal(true);
    setLoadingTransactions(true);
    try {
      const transactions = await adminService?.getMonthlyRevenueTransactions(selectedYear, selectedMonth);
      setRevenueTransactions(transactions || []);
    } catch (err) {
      console.error('Revenue transactions error:', err);
      setRevenueTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleDownloadRevenueCSV = () => {
    if (!revenueTransactions?.length) return;
    const monthNames = ['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık'];
    const headers = ['Tarih', 'Kategori', 'Müşteri / İşlem', 'Ödeme Yöntemi', 'Tutar (₺)'];
    const rows = revenueTransactions?.map(t => [
      new Date(t?.date)?.toLocaleDateString('tr-TR'),
      t?.category,
      `"${(t?.customer || '-')?.replace(/"/g, '""')}"`,
      t?.paymentMethod || '-',
      t?.amount?.toFixed(2)
    ]);
    const csvString = [headers?.join(';'), ...rows?.map(r => r?.join(';'))]?.join('\n');
    const fullString = "sep=;\n" + csvString;
    const encoder = new TextEncoder();
    const csvData = encoder?.encode(fullString);
    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, csvData], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link?.setAttribute('download', `aylik_gelir_${monthNames?.[selectedMonth]}_${selectedYear}.csv`);
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="main-content">
          <div className="max-w-[1536px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <p className="text-destructive font-medium">Hata: {error}</p>
              <Button onClick={loadDashboardData} className="mt-4">
                Yeniden Dene
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const SidebarContent = () => (
    <nav className="flex flex-col h-full">
      <div className="px-4 py-5 border-b border-border">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
            <Icon name="Shield" size={16} className="text-primary-foreground" />
          </div>
          <div>
            <p className="text-sm font-bold text-foreground">Admin Panel</p>
            <p className="text-xs text-muted-foreground">Yönetim Merkezi</p>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-3 px-2 space-y-1">
        {navCategories?.map((category) => (
          <div key={category?.id} className="mb-2">
            <div className="flex items-center gap-1.5 px-3 py-1.5 mb-1">
              <span className="text-xs">{category?.emoji}</span>
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{category?.label}</span>
            </div>
            {category?.items?.map((item) => {
              const isActive = activeTab === item?.id;
              return (
                <button
                  key={item?.id}
                  onClick={() => handleNavClick(item)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 text-left ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'text-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={item?.icon} size={16} className={isActive ? 'text-primary-foreground' : 'text-muted-foreground'} />
                  <span>{item?.label}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
      <div className="px-3 py-3 border-t border-border">
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start text-xs"
          iconName="Download"
          iconPosition="left"
          onClick={handleDownloadUserCSV}
        >
          Rapor İndir
        </Button>
      </div>
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="max-w-[1536px] mx-auto">
          <div className="lg:flex lg:min-h-[calc(100vh-64px)]">

            {/* Desktop Sidebar */}
            <aside className="hidden lg:flex flex-col w-64 flex-shrink-0 border-r border-border bg-card sticky top-16 h-[calc(100vh-64px)] overflow-hidden">
              <SidebarContent />
            </aside>

            {/* Mobile Overlay Backdrop */}
            {sidebarOpen && (
              <div
                className="fixed inset-0 bg-black/60 lg:hidden"
                style={{ zIndex: 9998 }}
                onClick={() => setSidebarOpen(false)}
              />
            )}

            {/* Mobile Sidebar Drawer */}
            <aside
              className={`fixed top-0 left-0 h-full lg:hidden transform transition-transform duration-300 ease-in-out ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
              }`}
              style={{
                zIndex: 9999,
                width: '75vw',
                maxWidth: '320px',
                backgroundColor: 'var(--color-card, #ffffff)',
                boxShadow: '4px 0 24px rgba(0,0,0,0.25)',
              }}
            >
              {/* Drawer Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border" style={{ backgroundColor: 'var(--color-card, #ffffff)' }}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 bg-primary rounded-lg flex items-center justify-center">
                    <Icon name="Shield" size={14} className="text-primary-foreground" />
                  </div>
                  <span className="font-semibold text-foreground text-sm">Admin Panel</span>
                </div>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-muted transition-colors"
                  aria-label="Menüyü kapat"
                >
                  <Icon name="X" size={18} className="text-foreground" />
                </button>
              </div>

              {/* Drawer Nav */}
              <div className="overflow-y-auto" style={{ height: 'calc(100% - 56px)', backgroundColor: 'var(--color-card, #ffffff)' }}>
                <div className="py-3 px-2 space-y-1">
                  {navCategories?.map((category) => (
                    <div key={category?.id} className="mb-2">
                      <div className="flex items-center gap-1.5 px-3 py-1.5 mb-1">
                        <span className="text-xs">{category?.emoji}</span>
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{category?.label}</span>
                      </div>
                      {category?.items?.map((item) => {
                        const isActive = activeTab === item?.id;
                        return (
                          <button
                            key={item?.id}
                            onClick={() => handleNavClick(item)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 text-left ${
                              isActive
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-foreground hover:bg-muted hover:text-foreground'
                            }`}
                          >
                            <Icon name={item?.icon} size={16} className={isActive ? 'text-primary-foreground' : 'text-muted-foreground'} />
                            <span>{item?.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ))}
                </div>
                <div className="px-3 py-3 border-t border-border">
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full justify-start text-xs"
                    iconName="Download"
                    iconPosition="left"
                    onClick={handleDownloadUserCSV}
                  >
                    Rapor İndir
                  </Button>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0 px-4 md:px-6 lg:px-8 py-6 md:py-8">

              {/* Mobile Top Bar */}
              <div className="flex items-center gap-3 mb-6 lg:hidden">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 rounded-lg bg-card border border-border hover:bg-muted transition-colors"
                >
                  <Icon name="Menu" size={20} />
                </button>
                <div>
                  <h1 className="text-lg font-bold text-foreground">Yönetim Paneli</h1>
                  <p className="text-xs text-muted-foreground">{getActiveLabel()}</p>
                </div>
              </div>

              {/* Desktop Page Header */}
              <div className="hidden lg:flex items-center justify-between mb-6 md:mb-8">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1">Yönetim Paneli</h1>
                  <p className="text-sm text-muted-foreground">Platform yönetimi ve analitik gösterge paneli</p>
                </div>
              </div>

              {activeTab === 'overview' && (
                <div className="space-y-6 md:space-y-8">

                  {/* Month/Year Filter */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <Icon name="CalendarDays" size={16} className="text-muted-foreground" />
                      <span className="text-sm font-medium text-foreground">Dönem Filtresi:</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedMonth}
                        onChange={(e) => handleMonthFilterChange(selectedYear, parseInt(e?.target?.value))}
                        className="text-sm border border-border rounded-lg px-3 py-1.5 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']?.map((m, i) => (
                          <option key={i} value={i}>{m}</option>
                        ))}
                      </select>
                      <select
                        value={selectedYear}
                        onChange={(e) => handleMonthFilterChange(parseInt(e?.target?.value), selectedMonth)}
                        className="text-sm border border-border rounded-lg px-3 py-1.5 bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                      >
                        {Array.from({ length: 5 }, (_, i) => now?.getFullYear() - i)?.map(y => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    {!isCurrentMonth && (
                      <button
                        onClick={() => handleMonthFilterChange(now?.getFullYear(), now?.getMonth())}
                        className="text-xs text-primary hover:underline font-medium"
                      >
                        Bu Aya Dön
                      </button>
                    )}
                    {loadingStats && (
                      <div className="w-4 h-4 border-2 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                    )}
                    {!isCurrentMonth && (
                      <span className="text-xs text-muted-foreground bg-warning/10 text-warning px-2 py-1 rounded-md font-medium">
                        Geçmiş Dönem Görüntüleniyor
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    <StatCard
                      title="Toplam Kullanıcı"
                      value={stats?.totalUsers?.toLocaleString('tr-TR')}
                      change="+12.5%"
                      changeType="positive"
                      icon="Users"
                      iconBg="bg-primary/10 text-primary"
                      trend="son 30 gün"
                      breakdown=""
                    />
                    <StatCard
                      title={isCurrentMonth ? 'Aylık Gelir' : `${['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']?.[selectedMonth]} ${selectedYear} Geliri`}
                      value={`₺${stats?.totalRevenue?.toLocaleString('tr-TR')}`}
                      change="+0%"
                      changeType="positive"
                      icon="TrendingUp"
                      iconBg="bg-success/10 text-success"
                      trend={isCurrentMonth ? 'son 30 gün' : `${['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']?.[selectedMonth]} ${selectedYear}`}
                      breakdown={`Mağaza: ₺${stats?.storeRevenue?.toLocaleString('tr-TR')}  •  Etkinlik: ₺${stats?.eventRevenue?.toLocaleString('tr-TR')}`}
                    />
                    <StatCard
                      title="Aktif Etkinlik"
                      value={stats?.activeEvents?.toLocaleString('tr-TR')}
                      change="+8.3%"
                      changeType="positive"
                      icon="Calendar"
                      iconBg="bg-secondary/10 text-secondary"
                      trend="bu ay"
                      breakdown=""
                    />
                    <StatCard
                      title="Bekleyen Onay"
                      value={stats?.pendingApprovals?.toLocaleString('tr-TR')}
                      change="-5.1%"
                      changeType="negative"
                      icon="AlertCircle"
                      iconBg="bg-warning/10 text-warning"
                      trend="son 7 gün"
                      breakdown=""
                    />
                  </div>

                  {/* All-Time Revenue Card */}
                  <div className="bg-gradient-to-r from-primary/5 to-success/5 rounded-xl border border-primary/20 p-4 md:p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon name="Infinity" size={16} className="text-primary" />
                          </div>
                          <p className="text-sm font-semibold text-foreground">Tüm Zamanlar Toplam Gelir</p>
                        </div>
                        <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                          ₺{allTimeRevenue?.allTimeTotal?.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                        </h3>
                        <div className="flex flex-wrap gap-4 mt-3">
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-success"></div>
                            <span className="text-sm text-muted-foreground">
                              Mağaza: <span className="font-semibold text-foreground">₺{allTimeRevenue?.allTimeStoreRevenue?.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2.5 h-2.5 rounded-full bg-secondary"></div>
                            <span className="text-sm text-muted-foreground">
                              Etkinlik: <span className="font-semibold text-foreground">₺{allTimeRevenue?.allTimeEventRevenue?.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</span>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right hidden sm:block">
                        <p className="text-xs text-muted-foreground">Platform başlangıcından bu yana</p>
                        <p className="text-xs text-muted-foreground mt-1">tüm onaylı işlemler dahil</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    <RevenueChart data={revenueData} type="line" />
                    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">Hızlı İşlemler</h2>
                      <div className="space-y-3">
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          iconName="Download"
                          iconPosition="left"
                          onClick={handleDownloadUserCSV}
                        >
                          Üye Listesi (CSV)
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          iconName="Megaphone"
                          iconPosition="left"
                          onClick={handleBroadcastNotification}
                        >
                          Duyuru Gönder
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          iconName="FileText"
                          iconPosition="left"
                          onClick={handleOpenRevenueDetail}
                        >
                          📄 Aylık Gelir Detayı (Liste)
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
                    <div className="bg-card rounded-xl border border-border p-4 md:p-6">
                      <h2 className="text-xl md:text-2xl font-semibold text-foreground mb-4">Son Aktiviteler</h2>
                      {loadingActivities ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="w-8 h-8 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                        </div>
                      ) : recentActivities?.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">Henüz aktivite yok</p>
                      ) : (
                        <div className="space-y-4">
                          {recentActivities?.map((activity, index) => (
                            <div key={index} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-smooth">
                              <div className={`w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 ${activity?.color}`}>
                                <Icon name={activity?.icon} size={16} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm text-foreground">{activity?.text}</p>
                                <p className="text-xs text-muted-foreground mt-1">{activity?.time}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'users' && (
                <UserManagementTable users={users} onUserAction={handleUserAction} />
              )}

              {activeTab === 'events' && (
                <EventManagementPanel events={events} onEventAction={(action, eventId) => console.log(`Event action: ${action} for event ${eventId}`)} />
              )}

              {activeTab === 'moderation' && (
                <ContentModerationPanel
                  items={[...moderationItems?.events, ...moderationItems?.marketplace]}
                  onModerationAction={handleModerationAction}
                />
              )}

              {activeTab === 'event-management' && <DetailedEventManagement />}
              {activeTab === 'store-management' && <StoreManager />}
              {activeTab === 'spot-management' && <SpotManagement />}
              {activeTab === 'coupon-management' && <CouponManagement />}

              {activeTab === 'community' && (
                <div className="space-y-6">
                  <Community onUserClick={(id) => setSelectedCommunityUserId(id)} />
                </div>
              )}

              {activeTab === 'blog' && <BlogManagement />}
              {activeTab === 'review-moderation' && <ModerationTab />}
              {activeTab === 'theme-logo' && <ThemeLogoManagement />}
              {activeTab === 'email-templates' && <EmailTemplateManager />}
              {activeTab === 'master-invoice-center' && <MasterInvoiceCenter embedded={true} />}
              {activeTab === 'invoice-audit' && <InvoiceAuditDashboard embedded={true} />}
              {activeTab === 'invoice-compliance' && <EventInvoiceComplianceDashboard embedded={true} />}
              {activeTab === 'mysoft-logs' && <MysoftSyncLogs />}
            </div>
          </div>
        </div>
      </main>

      {selectedCommunityUserId && (
        <UserProfileModal
          userId={selectedCommunityUserId}
          onClose={() => setSelectedCommunityUserId(null)}
        />
      )}

      {/* Monthly Revenue Detail Modal */}
      {showRevenueDetailModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 pt-24">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col overflow-hidden relative">
            {/* Header */}
            <div className="flex-none p-4 sm:p-6 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-foreground">
                  📄 Aylık Gelir Detayı
                </h2>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {['Ocak','Şubat','Mart','Nisan','Mayıs','Haziran','Temmuz','Ağustos','Eylül','Ekim','Kasım','Aralık']?.[selectedMonth]} {selectedYear} — Onaylı İşlemler
                </p>
              </div>
              <button
                onClick={() => setShowRevenueDetailModal(false)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Icon name="X" size={20} className="text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 sm:p-6">
              {loadingTransactions ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-10 h-10 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
                </div>
              ) : revenueTransactions?.length === 0 ? (
                <div className="text-center py-16 text-muted-foreground">
                  <Icon name="FileX" size={40} className="mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Bu dönemde onaylı işlem bulunamadı.</p>
                </div>
              ) : (
                <>
                  {/* Summary row */}
                  <div className="flex flex-wrap gap-3 mb-4">
                    <div className="bg-success/10 rounded-lg px-4 py-2 text-sm">
                      <span className="text-muted-foreground">Toplam: </span>
                      <span className="font-bold text-success">
                        ₺{revenueTransactions?.reduce((s, t) => s + t?.amount, 0)?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="bg-primary/10 rounded-lg px-4 py-2 text-sm">
                      <span className="text-muted-foreground">Mağaza: </span>
                      <span className="font-bold text-primary">
                        ₺{revenueTransactions?.filter(t => t?.category === 'Mağaza')?.reduce((s, t) => s + t?.amount, 0)?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="bg-secondary/10 rounded-lg px-4 py-2 text-sm">
                      <span className="text-muted-foreground">Etkinlik: </span>
                      <span className="font-bold text-secondary">
                        ₺{revenueTransactions?.filter(t => t?.category === 'Etkinlik')?.reduce((s, t) => s + t?.amount, 0)?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                    <div className="bg-muted rounded-lg px-4 py-2 text-sm text-muted-foreground">
                      {revenueTransactions?.length} işlem
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto rounded-lg border border-border">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="text-left px-3 py-2.5 font-semibold text-foreground whitespace-nowrap">Tarih</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-foreground whitespace-nowrap">Kategori</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-foreground">Müşteri / İşlem</th>
                          <th className="text-left px-3 py-2.5 font-semibold text-foreground whitespace-nowrap">Ödeme Yöntemi</th>
                          <th className="text-right px-3 py-2.5 font-semibold text-foreground whitespace-nowrap">Tutar</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {revenueTransactions?.map((t, idx) => (
                          <tr key={t?.id || idx} className="hover:bg-muted/30 transition-colors">
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                              {new Date(t?.date)?.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </td>
                            <td className="px-3 py-2.5 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                t?.category === 'Mağaza' ?'bg-primary/10 text-primary' :'bg-secondary/10 text-secondary'
                              }`}>
                                {t?.category === 'Mağaza' ? '🛒' : '🏄'} {t?.category}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-foreground max-w-xs truncate" title={t?.customer}>
                              {t?.customer || '-'}
                            </td>
                            <td className="px-3 py-2.5 text-muted-foreground whitespace-nowrap">
                              {t?.paymentMethod || '-'}
                            </td>
                            <td className="px-3 py-2.5 text-right font-semibold text-foreground whitespace-nowrap">
                              ₺{t?.amount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-muted/50 border-t-2 border-border">
                        <tr>
                          <td colSpan={4} className="px-3 py-2.5 font-bold text-foreground">Toplam</td>
                          <td className="px-3 py-2.5 text-right font-bold text-success">
                            ₺{revenueTransactions?.reduce((s, t) => s + t?.amount, 0)?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="flex-none p-4 border-t border-gray-100 bg-gray-50/50 flex items-center justify-between gap-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={handleDownloadRevenueCSV}
                disabled={!revenueTransactions?.length || loadingTransactions}
              >
                CSV İndir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowRevenueDetailModal(false)}
              >
                Kapat
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;